import { TutorialConfig, TutorialStep, TutorialTask } from '../types';

const ODOO_TASKS: TutorialTask[] = [
  { 
    id: 'task-1',
    runId: '00000000-0000-0000-0000-000000000001', 
    name: 'create sales order', 
    type: 'success' 
  },
  { 
    id: 'task-2',
    runId: '00000000-0000-0000-0000-000000000002', 
    name: 'configure tax settings', 
    type: 'failure' 
  },
  { 
    id: 'task-3',
    runId: '00000000-0000-0000-0000-000000000003', 
    name: 'add product to inventory', 
    type: 'fake_failure' 
  },
  { 
    id: 'task-4',
    runId: '00000000-0000-0000-0000-000000000004', 
    name: 'generate invoice', 
    type: 'fake_failure' 
  },
  { 
    id: 'task-5',
    runId: '00000000-0000-0000-0000-000000000005', 
    name: 'create customer account', 
    type: 'fake_success' 
  },
  { 
    id: 'task-6',
    runId: '00000000-0000-0000-0000-000000000006', 
    name: 'configure workflow automation', 
    type: 'bad_task' 
  },
  { 
    id: 'task-7',
    runId: '00000000-0000-0000-0000-000000000007', 
    name: 'optimize database performance', 
    type: 'bad_task' 
  }
];

const ODOO_STEPS: TutorialStep[] = [
  // Platform navigation instructions
  {
    id: "welcome",
    title: "Welcome to Odoo Evaluation!",
    content:
      "This tutorial will show you how to evaluate Odoo model runs. First, let's learn how to use the platform.",
    targetSelector: ".App-header h1",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-description",
    title: "Task Description",
    content:
      "This shows the instructions given to the AI model to complete inside Odoo. Read this carefully to understand what the model was asked to do.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "skip-to-frame-2",
    title: "Skip to Frame 2",
    content:
      "Click the right arrow button or press the RIGHT arrow key to go to the next frame.",
    targetSelector: ".nav-button:nth-child(3)",
    action: "seek",
    position: "top",
  },
  {
    id: "current-action",
    title: "Current Action",
    content:
      "This section describes what action the AI model is performing in the current frame. It helps you understand what the model is trying to do.",
    targetSelector: ".sidebar-section:nth-child(3)",
    action: "next",
    position: "right",
  },
  {
    id: "main-click",
    title: "Main Click Indicator",
    content:
      "The circle with ripple effect shows where the model clicked on the screen in this frame. This helps you track the model's interactions.",
    targetSelector: ".click-indicator",
    action: "next",
    position: "left",
  },
  {
    id: "next-click",
    title: "Next Click Preview",
    content:
      "The faded circle shows where the model will click in the next frame. This helps you anticipate the model's next action.",
    targetSelector: ".next-click-indicator",
    action: "next",
    position: "left",
  },
  {
    id: "go-to-frame-3",
    title: "Go to Next Frame",
    content:
      'Click the right arrow button to advance to frame 3. Notice how the previous "next click" becomes the current click.',
    targetSelector: ".nav-button:nth-child(3)",
    action: "seek",
    position: "top",
  },
  {
    id: "seek-backward",
    title: "Previous Frame",
    content:
      "Click the left arrow button or press the LEFT arrow key to go back to the previous frame.",
    targetSelector: ".nav-button:first-child",
    action: "seek",
    position: "top",
  },
  {
    id: "play",
    title: "Play the Video",
    content: "Click the play button or press SPACE to start playing the video.",
    targetSelector: ".play-pause-btn",
    action: "play",
    position: "top",
  },
  {
    id: "pause",
    title: "Pause the Video",
    content: "Click the pause button or press SPACE to pause the video.",
    targetSelector: ".play-pause-btn",
    action: "pause",
    position: "top",
  },
  {
    id: "speed-controls",
    title: "Speed Controls",
    content:
      "Use the speed buttons to control playback speed. This helps you carefully review complex actions.",
    targetSelector: ".speed-selector",
    action: "next",
    position: "top",
  },
  {
    id: "seek-timeline",
    title: "Timeline Scrubbing",
    content:
      "Click and drag on the timeline to quickly navigate to any frame. The blue bar shows your current position. Try it now, then click Next when ready.",
    targetSelector: ".timeline-container",
    action: "next",
    position: "top",
  },
  {
    id: "watch-before-eval",
    title: "Watch Before Evaluating",
    content:
      "Important: You must watch the entire video before evaluating. Let's jump to the final frame to see how evaluation works.",
    targetSelector: ".sidebar-section:nth-child(2)",
    action: "next",
    position: "right",
  },
  {
    id: "finalframe",
    title: "Jump to Final Frame",
    content:
      "Click on the timeline near the end to jump to the final frame. This shows the end result of the AI's work.",
    targetSelector: ".timeline-container",
    action: "next",
    position: "top",
  },
  // Task 1: Obvious Success
  {
    id: "task-1-intro",
    title: "Example 1: Obvious Success",
    content:
      "Let's start with an example of a clearly successful task completion. Watch how the AI creates a sales order in Odoo.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-1-watch",
    title: "Watch the AI Work",
    content:
      "Play the video or scrub through the timeline to see how the AI navigates through Odoo's interface to create a sales order with the specified products and customer.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-1-evaluate",
    title: "Evaluate This Success",
    content:
      "Now that you've watched the AI complete the task successfully, click Success to mark this as a success.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "success",
  },
  {
    id: "task-1-complete",
    title: "Good Job!",
    content:
      'That\'s correct! The AI successfully created the sales order as instructed. Click "Next task" to continue.',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },
  // Task 2: Obvious Failure
  {
    id: "task-2-intro",
    title: "Example 2: Obvious Failure",
    content:
      "Now let's see a clear failure. The AI needs to configure tax settings but fails to navigate the complex menu structure.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-2-watch",
    title: "Observe the Failure",
    content:
      "Watch the video to see how the AI struggles to find the tax configuration section and ultimately fails to set up the required tax rules.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-2-evaluate",
    title: "Evaluate This Failure",
    content:
      "The AI couldn't navigate to the correct settings and failed to configure any tax rules. This is an obvious failure. Click Fail to mark it correctly.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "fail",
  },
  {
    id: "task-2-complete",
    title: "Correct!",
    content:
      'You identified the failure correctly. Click "Next task" to continue.',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },
  // Task 3: False Failure (add product to inventory)
  {
    id: "task-3-intro",
    title: "Example 3: False Failure",
    content:
      "This example shows why it's crucial to watch until completion. The AI makes a mistake but then corrects it.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-3-watch-correction",
    title: "Watch the Correction",
    content:
      "Play through the video carefully. The AI initially enters the wrong product category, but then navigates back and corrects it. Don't judge too early!",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-3-evaluate",
    title: "Evaluate After Full Viewing",
    content:
      "The AI successfully corrected its mistake and added the product with all required information. This is actually a success. Click Success.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "success",
  },
  {
    id: "task-3-complete",
    title: "Well Done!",
    content:
      'You correctly identified this as a success despite the initial error. Always watch to completion! Click "Next task".',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },
  // Task 4: False Failure (generate invoice)
  {
    id: "task-4-intro",
    title: "Example 4: Hidden Success",
    content:
      "This task appears to fail but is actually successful. The issue is with how Odoo displays draft invoices.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-4-watch",
    title: "Watch and Notice",
    content:
      "Play the video and notice the AI generates an invoice but it remains in draft status.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-4-status-hint",
    title: "Check Invoice Status",
    content:
      "The invoice is created successfully but shows as 'Draft'. Since the task didn't specify posting the invoice, creating it in draft status fulfills the requirement.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-4-evaluate",
    title: "Evaluate Based on Task",
    content:
      "Since the task only asked to generate an invoice and didn't specify posting it, the draft invoice is a success. Click Success.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "success",
  },
  {
    id: "task-4-complete",
    title: "Excellent!",
    content:
      'You recognized that draft status doesn\'t indicate failure when not specified otherwise. Click "Next task".',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },
  // Task 5: Fake Success
  {
    id: "task-5-intro",
    title: "Example 5: Deceptive Success",
    content:
      "This final example looks successful but actually fails. The AI appears to create a customer account correctly.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-5-watch",
    title: "Watch Carefully",
    content:
      "Play through and watch the AI fill in all the customer information fields. It seems to do everything right.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-5-missing-save",
    title: "Look for Confirmation",
    content:
      "But notice something critical: the AI never clicks the Save button. If you check the customer list, this customer doesn't exist. The form was filled but never saved.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-5-evaluate",
    title: "Final Evaluation",
    content:
      "Despite filling all fields correctly, the customer was never saved. This is a failure. Click Fail.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "fail",
  },
  {
    id: "task-5-complete",
    title: "Good Analysis!",
    content:
      'You correctly identified the missing save action. Click "Next task" to continue.',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },
  // Task 6: Bad Task - Configure workflow automation
  {
    id: "task-6-intro",
    title: "Example 6: Ambiguous Workflow Task",
    content:
      "This task asks the AI to 'configure workflow automation for better efficiency'. Let's see why this is problematic.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-6-ambiguity",
    title: "Multiple Ambiguities",
    content:
      "'Better efficiency' is subjective - faster processing? Fewer steps? Less manual work? 'Workflow automation' could mean sales, inventory, accounting, or any other module. No specific metrics for success are provided.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-6-evaluate",
    title: "Mark as Bad Task",
    content:
      "This task has no clear success criteria and is too vague. Click Bad Task to mark it correctly.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "bad-task",
  },
  {
    id: "task-6-complete",
    title: "Excellent!",
    content:
      'You identified the ambiguous task correctly. Click "Next task" for one more example.',
    targetSelector: "button.next-task",
    action: "next-task-button",
    position: "top",
  },
  // Task 7: Bad Task - Optimize database performance
  {
    id: "task-7-intro",
    title: "Example 7: Technical Ambiguity",
    content:
      "This task asks to 'optimize database performance'. Watch for the technical ambiguities.",
    targetSelector: ".task-description",
    action: "next",
    position: "right",
  },
  {
    id: "task-7-scope-ambiguity",
    title: "Undefined Scope",
    content:
      "Should the AI optimize queries, indexes, or server settings? What performance metrics should improve - response time, throughput, or resource usage? What's the baseline to compare against?",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-7-measurement-ambiguity",
    title: "No Success Metrics",
    content:
      "Without specific performance targets or areas to optimize, there's no way to determine if the task was completed successfully. Any action could be considered right or wrong.",
    targetSelector: ".screenshot-wrapper",
    action: "next",
    position: "bottom",
  },
  {
    id: "task-7-evaluate",
    title: "Another Bad Task",
    content:
      "With no clear scope or measurable success criteria, this task is impossible to evaluate objectively. Click Bad Task.",
    targetSelector: ".evaluation-buttons",
    action: "evaluate",
    position: "top",
    expectedEvaluation: "bad-task",
  },
  {
    id: "tutorial-complete",
    title: "Tutorial Complete!",
    content:
      "Congratulations! You've learned to identify obvious successes/failures, false failures, fake successes, and ambiguous bad tasks in Odoo. Click below to start evaluating real Odoo tasks. Click on 'New Projects' to claim your first labeling project.",
    action: "next",
    position: "bottom",
  },
];

export const odooTutorialConfig: TutorialConfig = {
  software: 'odoo',
  displayName: 'Odoo',
  totalTasks: 7,
  tasks: ODOO_TASKS,
  steps: ODOO_STEPS,
  completionKey: 'odoo_onboarding_v1_completed',
  dataSource: 'api',
  // No custom fetcher needed - will use standard API calls
};